/**
 * Date Validation Utilities
 *
 * This file contains utility functions for validating dates and performing comparisons.
 * These functions return BOOLEAN values indicating validity, relationships, or conditions.
 * Use these utilities when you need to:
 * - Validate if a date is valid and parseable (e.g., isDateValid, isValidDate)
 * - Compare two dates (e.g., isAfter, isBefore, isSame, isBeforeToday)
 * - Check if dates fall within ranges (e.g., isDateWithinLast, isDateRangeIncludingToday)
 * - Verify date range overlaps (e.g., checkDateRangeOverlap)
 * - Test date properties (e.g., isWeekend, isOddDay, isQuarterCompleted)
 * - Validate user input before processing (e.g., check if date string is valid)
 *
 * Key distinction: These functions return TRUE/FALSE answers about dates, not new dates or values.
 */
import { DateTime as LuxonDateTime, type DateTimeUnit } from 'luxon'
import type { Duration } from '@/utils/date/conversion.js'
import { formatDurationToMs } from '@/utils/date/conversion.js'
import {
  getDateTime,
  getTodayDateTime,
  getTodayDateTimeISO,
  getTodayDateTimeISODate,
  getWeekday,
  getDayOfMonth,
} from '@/utils/date/getter.js'

/**
 * Verifies whether the given date is valid or not.
 * @param { Date | string | number | undefined } date - The date to be validated.
 * TODO: This function is deprecated, use `isValidDate` instead.
 */
export function isDateValid(date: Date | string | number | undefined) {
  if (!date) {
    return false
  }

  if (date instanceof Date) {
    return Number.isFinite(+date)
  }

  return !Number.isNaN(new Date(date).valueOf())
}

/**
 * Verifies if a given date string is valid and can be parsed.
 *
 * @param value - The date as a Date object or ISO string to be validated
 * @returns Boolean value indicating if the date is valid or not
 *
 * @example
 * ```typescript
 * isValidDate('2024-01-15') // Returns: true
 * isValidDate('invalid-date') // Returns: false
 * isValidDate(new Date('2024-01-15')) // Returns: true
 * ```
 */
export function isValidDate(value: Date | string) {
  return Boolean(getDateTime(value)?.isValid)
}

/**
 * Checks if the first date is after the second date (day-level comparison).
 * Compares dates at the start of day, ignoring time components.
 *
 * @param date - The date to check
 * @param dateToCompare - The date to compare against
 * @returns true if date is after dateToCompare, false otherwise
 *
 * @example
 * ```typescript
 * isAfter('2024-01-16', '2024-01-15') // Returns: true
 * isAfter('2024-01-15', '2024-01-16') // Returns: false
 * isAfter('2024-01-15T23:59:59', '2024-01-15T00:00:01') // Returns: false (same day)
 * ```
 */
export function isAfter(date: Date | string, dateToCompare: Date | string) {
  return (
    getDateTime(date).startOf('day') > getDateTime(dateToCompare).startOf('day')
  )
}

/**
 * Checks if the first date is before the second date (day-level comparison).
 * Compares dates at the start of day, ignoring time components.
 *
 * @param date - The date to check
 * @param dateToCompare - The date to compare against
 * @returns true if date is before dateToCompare, false otherwise
 *
 * @example
 * ```typescript
 * isBefore('2024-01-15', '2024-01-16') // Returns: true
 * isBefore('2024-01-16', '2024-01-15') // Returns: false
 * isBefore('2024-01-15T00:00:01', '2024-01-15T23:59:59') // Returns: false (same day)
 * ```
 */
export function isBefore(date: Date | string, dateToCompare: Date | string) {
  return (
    getDateTime(date).startOf('day') < getDateTime(dateToCompare).startOf('day')
  )
}

/**
 * Checks if two dates have the same value for a given unit (e.g., same day, month, year).
 *
 * @param {Date | string} date - The first date to compare.
 * @param {Date | string} dateToCompare - The second date to compare.
 * @param {DateTimeUnit} unit - The unit to compare ('year', 'month', 'day', 'hour', etc.).
 * @returns {boolean} - Returns true if both dates have the same value for the specified unit.
 */
export function isSame(
  date: Date | string,
  dateToCompare: Date | string,
  unit: DateTimeUnit,
) {
  return getDateTime(date).hasSame(getDateTime(dateToCompare), unit)
}

/**
 * Checks if a date is after today (day-level comparison).
 * Compares the date against the current date at the start of day, ignoring time.
 *
 * @param date - The date to check as a Date object or ISO string
 * @returns true if the date is after today, false otherwise
 *
 * @example
 * ```typescript
 * // Assuming today is 2024-01-15
 * isAfterToday('2024-01-16') // Returns: true
 * isAfterToday('2024-01-15') // Returns: false (same day)
 * isAfterToday('2024-01-14') // Returns: false
 * ```
 */
export function isAfterToday(date: Date | string) {
  const todayDate = LuxonDateTime.now().startOf('day').toISODate()
  return isAfter(date, todayDate)
}

/**
 * Checks if a given date is today (day-level comparison).
 * Convenience function that combines isSame and getTodayDateTimeISODate.
 *
 * @param date - The date to check as a Date object or ISO string
 * @returns true if the date is today, false otherwise
 *
 * @example
 * ```typescript
 * // Assuming today is 2024-01-15
 * isToday('2024-01-15') // Returns: true
 * isToday('2024-01-16') // Returns: false
 * isToday('2024-01-15T23:59:59') // Returns: true (same day)
 * ```
 */
export function isToday(date: Date | string) {
  return isSame(date, getTodayDateTimeISODate(), 'day')
}

/**
 * Checks if a date is before today (day-level comparison).
 * Compares the date against the current date at the start of day, ignoring time.
 *
 * @param date - The date to check as a Date object or ISO string
 * @returns true if the date is before today, false otherwise
 *
 * @example
 * ```typescript
 * // Assuming today is 2024-01-15
 * isBeforeToday('2024-01-14') // Returns: true
 * isBeforeToday('2024-01-15') // Returns: false (same day)
 * isBeforeToday('2024-01-16') // Returns: false
 * ```
 */
export function isBeforeToday(date: Date | string) {
  const todayDate = LuxonDateTime.now().startOf('day').toISODate()
  return isBefore(date, todayDate)
}

/**
 * Checks if the provided date string is after the current date and time.
 *
 * @param {string} date - The date string in ISO format (e.g., "2025-01-01T00:00:00.000Z").
 * @returns {boolean} - Returns true if the date is after the current date and time, false otherwise.
 */
export function isAfterNow(date: string) {
  const todayDateTime = getTodayDateTimeISO()
  return compareDateTimes('<', date, todayDateTime)
}

/**
 * Checks if the provided date string is before the current date and time.
 *
 * @param {string} date - The date string in ISO format (e.g., "2025-01-01T00:00:00.000Z").
 * @returns {boolean} - Returns true if the date is before the current date and time, false otherwise.
 */
export function isBeforeNow(date: string) {
  const todayDateTime = getTodayDateTimeISO()
  return compareDateTimes('>', date, todayDateTime)
}

/**
 * Checks if a date falls within the last specified duration from now.
 * Useful for checking if something happened recently.
 *
 * @param date - The date to check as a Date object or ISO string
 * @param duration - A Duration object specifying the time window (minutes, hours, or days)
 * @returns true if the date is within the last specified duration, false otherwise
 *
 * @example
 * ```typescript
 * // Assuming now is 2024-01-15T12:00:00
 * isDateWithinLast('2024-01-15T10:00:00', { hours: 3 }) // Returns: true (2 hours ago)
 * isDateWithinLast('2024-01-14T10:00:00', { hours: 3 }) // Returns: false (26 hours ago)
 * isDateWithinLast('2024-01-10', { days: 7 }) // Returns: true (5 days ago)
 * ```
 */
export function isDateWithinLast(date: Date | string, duration: Duration) {
  const dateObject = getDateTime(date)
  return dateObject.toMillis() > Date.now() - formatDurationToMs(duration)
}

/**
 * Checks if a date falls within the next specified duration from now.
 * Useful for checking if something will happen soon.
 *
 * @param date - The date to check as a Date object or ISO string
 * @param duration - A Duration object specifying the time window (minutes, hours, or days)
 * @returns true if the date is within the next specified duration, false otherwise
 *
 * @example
 * ```typescript
 * // Assuming now is 2024-01-15T12:00:00
 * isDateWithinNext('2024-01-15T14:00:00', { hours: 3 }) // Returns: true (in 2 hours)
 * isDateWithinNext('2024-01-16T14:00:00', { hours: 3 }) // Returns: false (in 26 hours)
 * isDateWithinNext('2024-01-20', { days: 7 }) // Returns: true (in 5 days)
 * ```
 */
export function isDateWithinNext(date: Date | string, duration: Duration) {
  const dateObject = getDateTime(date)
  return dateObject.toMillis() < Date.now() + formatDurationToMs(duration)
}

/**
 * Checks if the provided date range includes today.
 *
 * @param startDate - The start date in ISO format (e.g., "2025-01-01").
 * @param endDate - The end date in ISO format (e.g., "2025-01-02").
 * @returns {boolean} - Returns true if the date range includes today, false otherwise.
 */
export function isDateRangeIncludingToday(
  startDate?: string,
  endDate?: string | null,
) {
  if (!startDate || !endDate) {
    return false
  }

  const today = getTodayDateTimeISODate()
  const start = startDate
  const end = endDate

  const isAfterStart = compareDateTimes('<=', today, start)
  const isBeforeEnd = compareDateTimes('>=', today, end)

  return isAfterStart && isBeforeEnd
}

/**
 * Determines if a quarter has been completed based on the current date
 */
export function isQuarterCompleted(quarter: 1 | 2 | 3 | 4) {
  const today = getTodayDateTime()
  const currentQuarter = Math.ceil(today.month / 3)

  // If we're looking at a quarter before the current one, it's completed
  return quarter < currentQuarter
}

/**
 * Checks if a given date falls on a weekend (Saturday or Sunday).
 * @param date - The date to check as a Date object or string.
 * @returns true if the date is Saturday or Sunday, false otherwise.
 */
export function isWeekend(date: Date | string) {
  const dayOfWeek = getWeekday(date)
  return dayOfWeek === 6 || dayOfWeek === 7
}

/**
 * Checks if the day of the month for a given date is odd.
 * @param date - The date to check as a Date object or string.
 * @returns true if the day of the month is odd, false otherwise.
 */
export function isOddDay(date: Date | string) {
  const day = getDayOfMonth(date)
  return day % 2 === 1
}

/**
 * Checks if a date falls within a specified date range (inclusive).
 * Automatically handles inverted ranges where end is before start.
 *
 * @param date - The date to check as a Date object or ISO string
 * @param rangeStart - The start date of the range (or null if not set)
 * @param rangeEnd - The end date of the range (or null if not set)
 * @returns true if the date is within the range (inclusive), false otherwise
 *
 * @example
 * ```typescript
 * isDateInRange('2024-01-15', '2024-01-10', '2024-01-20') // Returns: true
 * isDateInRange('2024-01-25', '2024-01-10', '2024-01-20') // Returns: false
 * isDateInRange('2024-01-10', '2024-01-10', '2024-01-20') // Returns: true (inclusive)
 * isDateInRange('2024-01-15', '2024-01-20', '2024-01-10') // Returns: true (handles inverted range)
 * isDateInRange('2024-01-15', null, '2024-01-20') // Returns: false (missing range start)
 * ```
 */
export function isDateInRange(
  date: Date | string,
  rangeStart: string | null,
  rangeEnd: string | null,
) {
  let start = rangeStart
  let end = rangeEnd

  if (!start || !end) {
    return false
  }

  // If end date is before start date, invert the date range
  if (isBefore(end, start)) {
    ;[start, end] = [end, start]
  }

  return (
    isSame(date, start, 'day') ||
    (!isBefore(date, start) && !isBefore(end, date))
  )
}

/**
 * Compares two date and time values based on a specified comparison operator.
 * @param comparison - The comparison operator to use.
 * @param startDate - The start date in ISO format (YYYY-MM-DD).
 * @param endDate - The end date in ISO format (YYYY-MM-DD).
 * @param startTime - The start time in ISO format (HH:MM), optional.
 * @param endTime - The end time in ISO format (HH:MM), optional.
 * @returns - The result of the comparison.
 */
export function compareDateTimes(
  comparison: '>' | '>=' | '<' | '<=' | '===',
  startDate: string,
  endDate: string,
  startTime?: string,
  endTime?: string,
) {
  const startDateString = startTime ? `${startDate}T${startTime}` : startDate
  const endDateString = endTime ? `${endDate}T${endTime}` : endDate

  const startDateTime = LuxonDateTime.fromISO(startDateString)
  const endDateTime = LuxonDateTime.fromISO(endDateString)

  switch (comparison) {
    case '>':
      return endDateTime > startDateTime
    case '>=':
      return endDateTime >= startDateTime
    case '<':
      return endDateTime < startDateTime
    case '<=':
      return endDateTime <= startDateTime
    case '===':
      return endDateTime.equals(startDateTime)
    default:
      throw new Error('Invalid comparison operator')
  }
}

/**
 * Checks if two date ranges overlap.
 * Note: Single day date ranges (start1 === end1, or start2 === end2 ) are handled the same way as multi-day ranges.
 *
 * @param start1 - The start date of the first range in ISO format (e.g., "2025-01-01").
 * @param end1 - The end date of the first range in ISO format (e.g., "2025-01-02").
 * @param start2 - The start date of the second range in ISO format (e.g., "2025-01-03").
 * @param end2 - The end date of the second range in ISO format (e.g., "2025-01-04").
 * @returns boolean - Returns true if the date ranges overlap, false otherwise.
 */
export function checkDateRangeOverlap(
  start1: string,
  end1: string,
  start2: string,
  end2: string,
) {
  const start1Date = getDateTime(start1)
  const end1Date = getDateTime(end1)
  const start2Date = getDateTime(start2)
  const end2Date = getDateTime(end2)

  return start1Date <= end2Date && start2Date <= end1Date
}
